@if (subpoints()?.length && subpoints(); as subpoints) {
  <onyx-button variant="ghost" [size]="24" (click)="toggleSubpoints()">
    @for (icon of subpointsIcon().icons; track icon) {
      <onyx-icon class="f-muted" [name]="icon" [size]="16"></onyx-icon>
    }

    @if (subpointsIcon().hiddenIcons; as hiddenIcons) {
      <span class="f-paragraph-s f-semibold f-muted">{{ hiddenIcons }}</span>
    }

    <onyx-icon
      class="f-muted"
      [name]="
        subpointsExpanded()
          ? 'arrow-alt-up-outlined'
          : 'arrow-alt-down-outlined'
      "
      [size]="16"
    ></onyx-icon>
  </onyx-button>
}

@if (realization(); as realization) {
  <div class="realization-container">
    <div class="realization-wrapper">
      <ng-container *ngTemplateOutlet="transitIcon"></ng-container>

      <span
        class="summary realization {{
          realization.compliance
        }} f-body-s f-medium"
        [onyxTooltip]="realizationTooltip"
      >
        <span>{{ realization.remainingDistance | onyxDistance }}</span>

        <span>·</span>

        @if (
          realization.remainingTransitDuration;
          as remainingTransitDuration
        ) {
          <span>
            {{ 'labels.tt' | translate }}
            {{ remainingTransitDuration | duration: { units: ['h'] } }}
          </span>
        } @else {
          <span>
            {{ realization.remainingDuration | duration: { units: ['h'] } }}
          </span>
        }
      </span>

      @let offRouteData = telematicsItem()?.status?.offRouteData;
      @if (offRouteData && offRouteData.isCritical) {
        <div class="off-route f-body-s f-medium f-danger">
          <onyx-icon
            name="vehicle-off-route-filled"
            [size]="20"
            onyxTooltip="labels.vehicleOffRoute"
          ></onyx-icon>

          <span>{{ offRouteData.offRouteDistance | onyxDistance }}</span>
        </div>
      }
    </div>

    @if (showBordersButton()) {
      <onyx-button variant="transparent" [size]="24" (click)="toggleBorders()">
        <onyx-icon
          class="f-muted"
          name="border-crossing-filled"
          [size]="16"
        ></onyx-icon>

        <onyx-icon
          class="f-muted"
          [name]="
            bordersExpanded()
              ? 'arrow-alt-up-outlined'
              : 'arrow-alt-down-outlined'
          "
          [size]="16"
        ></onyx-icon>
      </onyx-button>
    }
  </div>

  <ng-template #realizationTooltip>
    <p class="tooltip">
      {{ `${I18N}.remainingDistance` | translate }}
      {{ realization.remainingDistance | onyxDistance }}
      <br />

      @if (realization.remainingTransitDuration) {
        {{ `${I18N}.remainingTransitDuration` | translate }}
        {{ realization.remainingTransitDuration | duration: { units: ['h'] } }}
        <br />
      }

      {{ `${I18N}.remainingDuration` | translate }}
      {{ realization.remainingDuration | duration: { units: ['h'] } }}
    </p>
  </ng-template>
}

<p class="summary f-body-s f-muted" [class.hidden]="realization()">
  @if (!realization()) {
    <ng-container *ngTemplateOutlet="transitIcon"></ng-container>

    <span>·</span>
  }

  @let summary = routeSummary();
  <span
    class="wrapper"
    [onyxTooltip]="totalSummaryTooltip"
    [onyxTooltipDisabled]="!summary"
  >
    <span>{{ summary ? (summary.distance | onyxDistance) : '?? km' }}</span>

    <span>·</span>

    @if (summary?.transitDuration; as transitDuration) {
      <span>
        {{ 'labels.tt' | translate }}
        {{ transitDuration | duration: { units: ['h'] } }}
      </span>
    } @else {
      <span>
        {{ summary ? (summary.duration | duration: { units: ['h'] }) : '?? h' }}
      </span>
    }

    @if (legType() === OnyxRouteLegType.VEHICLE && variant() === 'default') {
      <span>·</span>

      <span class="wrapper">
        <onyx-icon name="trend-down-filled" [size]="7"></onyx-icon>

        <span>
          {{ summary ? (summary.fuelConsumption / 100 | number) : '??' }}
          {{ fuelUnit() }}
        </span>
      </span>

      @if (context !== 'map') {
        <span>·</span>

        <span>
          {{
            summary
              ? (summary.totalCost | onyxAmount: 'resultCurrency')
              : '?? €'
          }}
        </span>
      }
    }
  </span>

  <ng-template #totalSummaryTooltip>
    @if (summary) {
      <p class="tooltip">
        {{ `${I18N}.totalDistance` | translate }}
        {{ summary.distance | onyxDistance }}
        <br />

        @if (summary.transitDuration) {
          {{ `${I18N}.totalTransitDuration` | translate }}
          {{ summary.transitDuration | duration: { units: ['h'] } }}
          <br />
        }

        {{ `${I18N}.totalDuration` | translate }}
        {{ summary.duration | duration: { units: ['h'] } }}
        <br />

        @if (
          legType() === OnyxRouteLegType.VEHICLE && variant() === 'default'
        ) {
          {{ `${I18N}.totalFuelConsumption` | translate }}
          {{ summary.fuelConsumption / 100 | number }}
          {{ fuelUnit() }}
          <br />

          @if (context === 'order') {
            {{ `${I18N}.totalCost` | translate }}
            {{ summary.totalCost | onyxAmount: 'resultCurrency' }}
          }
        }
      </p>
    }
  </ng-template>
</p>

@if (hasAdd()) {
  <div class="row-actions" [class.attached]="actionsAttached()">
    <onyx-icon-button
      variant="ghost"
      color="black"
      [size]="24"
      icon="add-outlined"
      [circle]="true"
      onyxTooltip="labels.addPoint"
      [onyxDropdownOptions]="addOptions$ | async"
      (onyxDropdownSelectedValueChange)="$event.value?.(index())"
      (onyxDropdownAttachedChange)="actionsAttached.set($event)"
    ></onyx-icon-button>
  </div>
}

<ng-template #transitIcon>
  @let transitTypes = summary?.transitTypes?.length && summary?.transitTypes;
  @let value =
    transitTypes
      ? transitTypes.length === 1
        ? `${transitTypes[0]}`
        : 'ferry-train'
      : 'road';
  @let icon = `${value}-filled`;

  <div class="f-muted transit-icons">
    @if (context === 'routeCalculator' && transitTypes) {
      <onyx-icon
        name="road-filled"
        [size]="16"
        [onyxTooltip]="`${I18N}.transitTypes.road-filled`"
      ></onyx-icon>

      <span>·</span>

      <onyx-icon
        name="ferry-train-filled"
        [size]="16"
        [onyxTooltip]="`${I18N}.transitTypes.ferry-train-filled`"
      ></onyx-icon>
    } @else {
      <onyx-icon
        [name]="$any(icon)"
        [size]="16"
        [onyxTooltip]="`${I18N}.transitTypes.${value}-filled`"
      ></onyx-icon>
    }
  </div>
</ng-template>
